/**
 * The 50 US states and their land-border adjacency, used for the Poli Poker
 * "contiguous" (Contig) scoring bonus.
 *
 * Contiguity rule (per Scott, 2026-09): the continental USA is connected by
 * real land borders; Alaska and Hawaii are contiguous with EACH OTHER only
 * (not with the mainland). (This supersedes the older scoring-doc note that
 * paired HI↔CA and AK↔WA.)
 *
 * Four Corners: states meeting only at a single point (AZ–CO and NM–UT) are
 * NOT treated as bordering.
 */

export interface StateDef {
  code: string;
  name: string;
  borders: string[];
}

// Raw adjacency (may be listed one-directionally); made symmetric below.
const RAW: Array<[string, string, string[]]> = [
  ["AL", "Alabama", ["FL", "GA", "MS", "TN"]],
  ["AK", "Alaska", ["HI"]], // AK↔HI island pair only
  ["AZ", "Arizona", ["CA", "NV", "UT", "NM"]],
  ["AR", "Arkansas", ["LA", "MS", "MO", "OK", "TN", "TX"]],
  ["CA", "California", ["AZ", "NV", "OR"]],
  ["CO", "Colorado", ["KS", "NE", "NM", "OK", "UT", "WY"]],
  ["CT", "Connecticut", ["MA", "NY", "RI"]],
  ["DE", "Delaware", ["MD", "NJ", "PA"]],
  ["FL", "Florida", ["AL", "GA"]],
  ["GA", "Georgia", ["AL", "FL", "NC", "SC", "TN"]],
  ["HI", "Hawaii", ["AK"]], // AK↔HI island pair only
  ["ID", "Idaho", ["MT", "NV", "OR", "UT", "WA", "WY"]],
  ["IL", "Illinois", ["IN", "IA", "KY", "MO", "WI"]],
  ["IN", "Indiana", ["IL", "KY", "MI", "OH"]],
  ["IA", "Iowa", ["IL", "MN", "MO", "NE", "SD", "WI"]],
  ["KS", "Kansas", ["CO", "MO", "NE", "OK"]],
  ["KY", "Kentucky", ["IL", "IN", "MO", "OH", "TN", "VA", "WV"]],
  ["LA", "Louisiana", ["AR", "MS", "TX"]],
  ["ME", "Maine", ["NH"]],
  ["MD", "Maryland", ["DE", "PA", "VA", "WV"]],
  ["MA", "Massachusetts", ["CT", "NH", "NY", "RI", "VT"]],
  ["MI", "Michigan", ["IN", "OH", "WI"]],
  ["MN", "Minnesota", ["IA", "ND", "SD", "WI"]],
  ["MS", "Mississippi", ["AL", "AR", "LA", "TN"]],
  ["MO", "Missouri", ["AR", "IA", "IL", "KS", "KY", "NE", "OK", "TN"]],
  ["MT", "Montana", ["ID", "ND", "SD", "WY"]],
  ["NE", "Nebraska", ["CO", "IA", "KS", "MO", "SD", "WY"]],
  ["NV", "Nevada", ["AZ", "CA", "ID", "OR", "UT"]],
  ["NH", "New Hampshire", ["ME", "MA", "VT"]],
  ["NJ", "New Jersey", ["DE", "NY", "PA"]],
  ["NM", "New Mexico", ["AZ", "CO", "OK", "TX"]],
  ["NY", "New York", ["CT", "MA", "NJ", "PA", "VT"]],
  ["NC", "North Carolina", ["GA", "SC", "TN", "VA"]],
  ["ND", "North Dakota", ["MN", "MT", "SD"]],
  ["OH", "Ohio", ["IN", "KY", "MI", "PA", "WV"]],
  ["OK", "Oklahoma", ["AR", "CO", "KS", "MO", "NM", "TX"]],
  ["OR", "Oregon", ["CA", "ID", "NV", "WA"]],
  ["PA", "Pennsylvania", ["DE", "MD", "NJ", "NY", "OH", "WV"]],
  ["RI", "Rhode Island", ["CT", "MA"]],
  ["SC", "South Carolina", ["GA", "NC"]],
  ["SD", "South Dakota", ["IA", "MN", "MT", "ND", "NE", "WY"]],
  ["TN", "Tennessee", ["AL", "AR", "GA", "KY", "MO", "MS", "NC", "VA"]],
  ["TX", "Texas", ["AR", "LA", "NM", "OK"]],
  ["UT", "Utah", ["AZ", "CO", "ID", "NV", "WY"]],
  ["VT", "Vermont", ["MA", "NH", "NY"]],
  ["VA", "Virginia", ["KY", "MD", "NC", "TN", "WV"]],
  ["WA", "Washington", ["ID", "OR"]],
  ["WV", "West Virginia", ["KY", "MD", "OH", "PA", "VA"]],
  ["WI", "Wisconsin", ["IA", "IL", "MI", "MN"]],
  ["WY", "Wyoming", ["CO", "ID", "MT", "NE", "SD", "UT"]],
];

const borderSets = new Map<string, Set<string>>(RAW.map(([code]) => [code, new Set<string>()]));
for (const [code, , borders] of RAW) {
  for (const b of borders) {
    borderSets.get(code)!.add(b);
    borderSets.get(b)?.add(code); // enforce symmetry
  }
}

export const STATES: StateDef[] = RAW.map(([code, name]) => ({
  code,
  name,
  borders: [...borderSets.get(code)!].sort(),
}));

export const STATE_BY_CODE: Map<string, StateDef> = new Map(STATES.map((s) => [s.code, s]));

export const STATE_CODES: string[] = STATES.map((s) => s.code);

export function borders(a: string, b: string): boolean {
  return STATE_BY_CODE.get(a)?.borders.includes(b) ?? false;
}
